import { CompassIcon, CrownIcon, LandmarkIcon, LayersIcon, LockKeyholeIcon, MapIcon, SearchIcon, UsersIcon } from 'lucide-react';
import type { Service } from '../types/content';

export const services: Service[] = [
  {
    slug: 'executive-search',
    title: 'Executive Search',
    short: 'Research-led identification and direct engagement of experienced leaders for strategically important roles.',
    headline: 'Finding the leaders your strategy depends on.',
    description:
      'Executive search is a structured, research-led discipline. Rather than relying on whoever happens to be applying, we define the leadership profile your organization genuinely needs, map the relevant market, and engage experienced leaders directly — including those who are not actively seeking a move. Every search is shaped by your strategy, your culture, and the outcomes the role must deliver.',
    exampleRoles: ['Managing Director', 'General Manager', 'Regional President', 'Divisional Head', 'Country Manager'],
    needs: [
      'A newly created leadership role tied to growth or transformation',
      'Replacing a departing leader while protecting continuity',
      'Reaching leaders beyond your existing networks',
      'Hiring for a market or capability new to the organization',
    ],
    methodology: [
      'Briefing on strategy, culture, and measures of success',
      'Role specification and target-organization mapping',
      'Direct, discreet engagement of potential candidates',
      'Structured evaluation and a considered shortlist',
      'Interview coordination, offer, and appointment support',
    ],
    icon: SearchIcon,
  },
  {
    slug: 'c-suite-recruitment',
    title: 'C-Suite Recruitment',
    short: 'Search for CEOs, CFOs, COOs, CTOs, CHROs, and the executive officers who set direction.',
    headline: 'Appointments that shape the whole organization.',
    description:
      'C-suite appointments influence strategy, culture, stakeholder confidence, and the direction of entire organizations. We approach them with the rigour they demand: aligning stakeholders on the mandate, understanding the dynamics of the existing leadership team, and assessing candidates for judgment and leadership style as carefully as for functional experience.',
    exampleRoles: ['Chief Executive Officer', 'Chief Financial Officer', 'Chief Operating Officer', 'Chief Information Officer', 'Chief Technology Officer', 'Chief Human Resources Officer'],
    needs: [
      'A planned or unplanned chief executive transition',
      'Strengthening financial leadership ahead of investment or growth',
      'Appointing a first technology or digital executive',
      'Rebalancing the executive team after restructuring',
    ],
    methodology: [
      'Stakeholder alignment with the board and leadership team',
      'Leadership-team context and mandate definition',
      'Targeted mapping of executive talent',
      'In-depth assessment of experience, judgment, and fit',
      'Confidential presentation and appointment support',
    ],
    icon: CrownIcon,
  },
  {
    slug: 'board-search',
    title: 'Board and Non-Executive Director Search',
    short: 'Directors who bring governance, oversight, and strategic perspective to the board.',
    headline: 'Board composition built with intent.',
    description:
      'Effective boards are built deliberately. We work with chairs, nomination committees, and leadership teams to understand the skills, experience, and perspectives a board requires — governance, risk oversight, sector knowledge, or strategic expertise — and identify directors able to contribute meaningfully from their first meeting.',
    exampleRoles: ['Non-Executive Director', 'Independent Director', 'Board Chair', 'Audit Committee Chair', 'Advisory Board Member', 'Trustee'],
    needs: [
      'Addressing a skills or experience gap on the board',
      'Meeting independence or committee requirements',
      'Preparing the board for expansion, investment, or listing',
      'Planned board renewal and chair succession',
    ],
    methodology: [
      'Board skills and composition review',
      'Director profile and committee requirements',
      'Search across executive and governance networks',
      'Independence and conflict-of-interest considerations',
      'Introductions to the chair and nomination committee',
    ],
    icon: LandmarkIcon,
  },
  {
    slug: 'senior-leadership',
    title: 'Senior Leadership Recruitment',
    short: 'Functional and divisional leaders who carry strategy into day-to-day delivery.',
    headline: 'The leaders who turn strategy into results.',
    description:
      'Beneath the executive committee sit the leaders who translate strategy into delivery: functional heads, divisional directors, and senior specialists. We bring the same structured approach to these roles, recognising that they are often where an organization’s future executives are developed.',
    exampleRoles: ['Vice President', 'Finance Director', 'Head of Operations', 'Commercial Director', 'HR Director', 'Head of Technology'],
    needs: [
      'Scaling a function to match organizational growth',
      'Building a stronger bench beneath the executive team',
      'Establishing leadership in a new region or division',
      'Replacing critical capability after a departure',
    ],
    methodology: [
      'Functional briefing with the hiring executive',
      'Capability profile and reporting-line context',
      'Market mapping across comparable organizations',
      'Competency-based evaluation of candidates',
      'Shortlist presentation and interview support',
    ],
    icon: UsersIcon,
  },
  {
    slug: 'confidential-search',
    title: 'Confidential Search Assignments',
    short: 'Discreet searches where information is shared carefully and only on a need-to-know basis.',
    headline: 'Discretion designed into every step.',
    description:
      'Some appointments cannot be announced: succeeding an incumbent, preparing for a transaction, or building a team ahead of a market entry. In a confidential search, information about the organization and role is shared in controlled stages, candidates are engaged under clear confidentiality expectations, and communication is limited to agreed stakeholders.',
    exampleRoles: ['Successor to an incumbent executive', 'Leader for an unannounced venture', 'Executive for a pre-transaction team', 'Turnaround or transformation leader'],
    needs: [
      'Replacing a leader before the change is made public',
      'Protecting commercially sensitive plans',
      'Avoiding disruption to teams, clients, or markets',
      'Engaging candidates who require the same discretion',
    ],
    methodology: [
      'Agreed confidentiality protocol and stakeholder list',
      'Anonymised role briefing for early conversations',
      'Staged disclosure as candidate interest is confirmed',
      'Careful handling of client and candidate information',
      'Controlled communication through to appointment',
    ],
    icon: LockKeyholeIcon,
  },
  {
    slug: 'talent-mapping',
    title: 'Talent Mapping and Market Intelligence',
    short: 'Structured research into leadership talent pools, team structures, and market availability.',
    headline: 'Understand the market before you enter it.',
    description:
      'Talent mapping gives organizations a clear picture of the leadership landscape before — or instead of — a live search. Through structured research we identify where relevant leaders sit, how comparable organizations structure their teams, and what the realistic talent pool looks like for a given role or capability.',
    exampleRoles: ['Future executive succession candidates', 'Specialist leaders in emerging capabilities', 'Leadership in comparable or adjacent organizations', 'Regional leadership talent pools'],
    needs: [
      'Planning future appointments ahead of need',
      'Testing the feasibility of a role before committing',
      'Benchmarking internal leaders against the market',
      'Understanding leadership structures in a new market',
    ],
    methodology: [
      'Agree research scope, markets, and roles',
      'Identify target organizations and leadership structures',
      'Map relevant leaders and their career context',
      'Analyse the talent pool against your requirements',
      'Present findings and recommended next steps',
    ],
    icon: MapIcon,
  },
  {
    slug: 'leadership-advisory',
    title: 'Leadership Talent Advisory',
    short: 'Guidance on leadership requirements, organizational needs, and executive hiring strategy.',
    headline: 'Clarity before commitment.',
    description:
      'Before beginning a search, many organizations benefit from stepping back. Leadership talent advisory helps define what a role really needs to accomplish, how it fits within the wider leadership structure, and which hiring strategy suits the situation — from role design to the sequencing of multiple appointments.',
    exampleRoles: ['Newly created executive positions', 'Combined or restructured leadership roles', 'Sequenced leadership-team appointments', 'Interim-to-permanent transitions'],
    needs: [
      'Uncertainty about the right profile for a role',
      'Restructuring a leadership team',
      'Deciding between internal and external appointments',
      'Planning several senior hires in sequence',
    ],
    methodology: [
      'Conversations with key stakeholders',
      'Review of strategy and organizational structure',
      'Definition of leadership requirements',
      'Hiring strategy and sequencing recommendations',
      'Ongoing guidance as decisions are made',
    ],
    icon: CompassIcon,
  },
  {
    slug: 'succession-planning',
    title: 'Succession Planning Support',
    short: 'Preparing for future leadership transitions with structured readiness planning.',
    headline: 'Preparing for the next chapter before it begins.',
    description:
      'Leadership transitions are inevitable; disruption is not. We support organizations in identifying critical roles, understanding the readiness of internal successors, and benchmarking against the external market, so that future transitions are planned rather than reactive.',
    exampleRoles: ['Chief executive succession', 'Executive committee succession', 'Critical functional leadership roles', 'Board chair succession'],
    needs: [
      'No clear successor for a critical role',
      'An upcoming planned retirement or transition',
      'Board or investor focus on leadership resilience',
      'Developing internal leaders for future roles',
    ],
    methodology: [
      'Identify critical and high-risk roles',
      'Define future leadership requirements',
      'Review internal readiness with leadership',
      'Benchmark against the external market',
      'Shape a practical succession roadmap',
    ],
    icon: LayersIcon,
  },
];
